import React, {useEffect} from 'react';
import styled from "styled-components";
import Grid from "@material-ui/core/Grid";
import {useFormik} from "formik";
import logoColor from "../img/logo-color.png";
import {useDispatch} from "react-redux";
import {AuthService} from "../services/auth.service";
import {StyledInput} from '../components/form/input';
import {StyledButton} from "../components/ui-base/button";
import {PublisherService} from "../services/publisher.service";
import {showSnackError, showSnackSuccess} from "../store/global/global-slice";
import axios from "axios";
import {StyledSelect} from "../components/form/select";
import * as Yup from 'yup';
import LoginWrapper from "../components/login-wrapper";
import blankProfile from "../img/blank-profile.png";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {AccountTypeEnum} from "../models/account-type.enum";
import {AccountRegisterSubTypeEnum} from "../models/account-register-sub-type.enum";
import {StyledH2, StyledP3} from "../components/ui-base/styles";
import {StyledCheckbox} from "../components/form/checkbox";

const RegisterSchema = Yup.object().shape({
    password: Yup.string()
        .required("inputRequiredError"),
    passwordCheck: Yup.string().equals([Yup.ref('password')], 'register.form.inputErrors.passwordCheck')
        .required("inputRequiredError"),
    email: Yup.string().email("inputEmailError").required("inputRequiredError"),
    regulations: Yup
        .boolean()
        .oneOf([true], "inputRequiredError")
        .required("inputRequiredError")
});

const RegisterPage = () => {
    const [publisherLogo, setPublisherLogo] = React.useState(null);
    const [publisher, setPublisher] = React.useState(null);
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const {t} = useTranslation('common');

    const publisherAlias = queryParams && ((queryParams.get('publisher')) as string || null);

    useEffect(() => {
        const getProfile = async () => {
            const profile = await PublisherService.getProfileByAlias(publisherAlias);

            if (profile) {
                setPublisher(profile);
                setPublisherLogo(profile.logo?.file || blankProfile);
            } else {
                dispatch(showSnackError(t('register.notPublisher')));
            }
        }

        if (publisherAlias) {
            getProfile();
        }
    }, [dispatch, publisherAlias, t])

    const registerForm = useFormik({
        initialValues: {
            email: '',
            password: '',
            passwordCheck: '',
            subscriptionType: {id: AccountRegisterSubTypeEnum.trialPublisher90d, label: t('trialPublisher90d')},
            accountType: {code: AccountTypeEnum.AUT, label: t('author')},
            regulations: false
        },
        validationSchema: RegisterSchema,
        onSubmit: async values => {
            const res = await AuthService.register('', values.email, values.password, values.accountType.code, values.subscriptionType.id, publisherAlias || null);

            if (res) {
                if (publisher) {
                    const token = await AuthService.login('', values.password);

                    if (token) {
                        history.push('/first-login?publisher=' + publisherAlias);

                        axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                        dispatch(showSnackSuccess(t('register.authorCreated')));
                    }
                } else {
                    dispatch(showSnackSuccess(t('register.pubCreated')));
                    history.push('/login');
                }
            }
        }
    });

    return (
        <LoginWrapper pageType={'register'} children={
            <>
                <StyledGridLogoContainer container direction={"row"} justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <StyledLogo src={logoColor} alt="Logo"/>
                    </Grid>

                    {publisherAlias && publisherLogo &&
                        <StyledWrapperLogoPublisher item>
                            <StyledLogoPublisher src={publisherLogo} alt="Logo"/>
                        </StyledWrapperLogoPublisher>
                    }
                </StyledGridLogoContainer>

                <StyledH2>{t('register.register')}</StyledH2>

                <StyledGridSmallLoginContainer container direction={"column"} style={{marginTop: '1rem'}}>
                    <form onSubmit={registerForm.handleSubmit} noValidate>
                        <Grid
                            container
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                        >
                            {!publisherAlias &&
                                <StyledGridItemWrapper item>
                                    <StyledSelect
                                        onChange={registerForm.handleChange}
                                        value={registerForm.values.accountType}
                                        label={t('register.form.type') + ' *'}
                                        id={'accountType'}
                                        options={[
                                            {code: AccountTypeEnum.AUT, label: t('author')},
                                            {code: AccountTypeEnum.PUB, label: t('publisher')},
                                            // {code: AccountTypeEnum.AGENCY, label: t('agency')}
                                        ]}
                                        optionsLabel={(option) => option.label}
                                        showEmpty={false}
                                    />
                                </StyledGridItemWrapper>
                            }

                            <StyledGridItemWrapper item>
                                <StyledInput
                                    id={'email'}
                                    label={t('register.form.email') + ' *'}
                                    onChange={registerForm.handleChange}
                                    value={registerForm.values.email}
                                    error={registerForm.errors.email && registerForm.touched.email}
                                    errorMessage={t(registerForm.errors.email)}
                                />
                            </StyledGridItemWrapper>

                            {registerForm.values.accountType.code !== AccountTypeEnum.AUT &&
                                <StyledGridItemWrapper item>
                                    <StyledSelect
                                        onChange={registerForm.handleChange}
                                        value={registerForm.values.subscriptionType}
                                        label={t('register.form.subType')}
                                        id={'subscriptionType'}
                                        options={[{
                                            id: AccountRegisterSubTypeEnum.trialPublisher90d,
                                            label: t('trialPublisher90d')
                                        }, {
                                            id: AccountRegisterSubTypeEnum.trialPublisher100x,
                                            label: t('trialPublisher100x')
                                        }]}
                                        optionsLabel={(option) => option.label}
                                        showEmpty={false}
                                    />
                                </StyledGridItemWrapper>
                            }

                            <StyledGridItemWrapper item>
                                <StyledInput
                                    id={'password'}
                                    label={t('register.form.password') + ' *'}
                                    type={"password"}
                                    onChange={registerForm.handleChange}
                                    value={registerForm.values.password}
                                    error={registerForm.errors.password && registerForm.touched.password}
                                    errorMessage={t(registerForm.errors.password)}
                                />
                            </StyledGridItemWrapper>

                            <StyledGridItemWrapper item>
                                <StyledInput
                                    id={'passwordCheck'}
                                    label={t('register.form.rePassword') + ' *'}
                                    type={"password"}
                                    onChange={registerForm.handleChange}
                                    value={registerForm.values.passwordCheck}
                                    error={registerForm.errors.passwordCheck && registerForm.touched.passwordCheck}
                                    errorMessage={t(registerForm.errors.passwordCheck)}
                                />
                            </StyledGridItemWrapper>

                            <StyledGridItemWrapper item>
                                <StyledP3>{t('register.passwordInfo')}</StyledP3>
                            </StyledGridItemWrapper>

                            <StyledGridItemWrapperCheckbox item>
                                <StyledCheckbox
                                    id="regulations"
                                    name="regulations"
                                    onChange={registerForm.handleChange}
                                    value={registerForm.values.regulations}
                                    error={registerForm.errors.regulations && registerForm.touched.regulations}
                                    errorMessage={t(registerForm.errors.regulations)}
                                    // @ts-ignore
                                    label={
                                        <>
                                            {t('register.form.accept')} &nbsp;
                                            <a href={t('register.form.regulationsUrl')} target="_blank" rel="noreferrer">
                                                {t('register.form.regulations')}
                                            </a>
                                            &nbsp;{t('and')}&nbsp;
                                            <a href={t('register.form.privacyPolicyUrl')} target="_blank" rel="noreferrer">
                                                {t('register.form.privacyPolicy')}
                                            </a>.
                                        </>
                                    }
                                />
                            </StyledGridItemWrapperCheckbox>

                            <StyledGridItemWrapper item>
                                <StyledButton type="submit" color={'primary'}>{t('register.form.button')}</StyledButton>
                            </StyledGridItemWrapper>
                        </Grid>
                    </form>
                </StyledGridSmallLoginContainer>
            </>
        }/>
    );
};

const StyledGridItemWrapper = styled(Grid)`
  width: 100% !important;

  button {
    width: 100%;
  }
`;

const StyledGridItemWrapperCheckbox = styled(StyledGridItemWrapper)`
  padding-top: 0 !important;
  padding-bottom: 0 !important;
`;

const StyledGridLogoContainer = styled(Grid)`
  max-width: 700px;
  width: 100%;
  margin: 0 auto 2rem;
`;

const StyledLogo = styled('img')`
  height: 40px;
`;

const StyledLogoPublisher = styled('img')`
  max-height: 60px;
  width: auto;
`;

const StyledWrapperLogoPublisher = styled(Grid)`
  padding: 1rem;
`;

const StyledGridSmallLoginContainer = styled(Grid)`
  max-width: 500px;
  margin: 0 auto;
`;

export default RegisterPage;
